import { isAnEmail } from '@eventbrite/validators';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import {
    ILookUpUserApiQueryProps,
    ILookUpUserApiResponseProps,
    lookUpUser,
} from '../api/lookup';
import { SIGN_IN_METHOD_PASSWORD } from '../constants/index';
import { IAllContextsProps } from '../constants/interfaces';
import { DEFAULT_ERROR_MESSAGE } from '../constants/loginMessages';
import { PASSWORD_TOKEN_EXPIRED_MESSAGE } from '../constants/lookupMessages';
import { AccountTypes } from '../contexts/UserContext';
import { trackEnterEmailExistingAccount } from '../tracking/login';
import {
    handleLookupUserResponseForLinking,
    handleLookupUserResponseSocial,
    handleUserIsSocial,
    handleUserIsSoftAccount,
    handleUserNonExistent,
} from './lookupHandlers';
import { handleSignupUserResponse } from './signupHandlers';

export interface ILookUpUserActionProps {
    data: ILookUpUserApiQueryProps;
    isSignup?: boolean;
    contexts: IAllContextsProps;
    socialProvider?: string;
}

/**
 * Main logic for the lookup action
 * @param {Object} arguments - arguments passed to the function.
 * @param {string} arguments.email - The email of the user.
 * @param {string} arguments.sub - Special key for apple, Undefined if not coming from apple.
 * @param {boolean} arguments.isSignup - Signals if coming from the 'creat account' flow.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */
export const lookUpUserAction = async ({
    data,
    isSignup,
    socialProvider = '',
    contexts,
}: ILookUpUserActionProps) => {
    const { email, sub } = data;
    const {
        frontendContext,
        notificationsContext,
        configContext,
        userContext,
    } = contexts;
    const externalActions = configContext?.lookupMethods;

    userContext?.methods.lookup.setLookedAccount(email);

    if (email && isAnEmail(email)) {
        frontendContext?.methods.setLookupOngoing(true);

        externalActions?.attempt?.();

        return lookUpUser({ email, socialProvider, sub })
            .then((response: ILookUpUserApiResponseProps) => {
                externalActions?.success?.default?.(response);
                userContext?.methods.lookup.setLookupResponse(response);

                if (socialProvider) {
                    externalActions?.success?.social?.();

                    return handleLookupUserResponseSocial({
                        response,
                        socialProvider,
                        email,
                        contexts,
                    });
                } else {
                    if (isSignup) {
                        externalActions?.success?.signup?.();

                        handleSignupUserResponse({ response, email, contexts });
                    } else {
                        handleLookupUserResponse({ response, email, contexts });
                    }
                }
            })
            .catch(() => {
                externalActions?.error?.();

                notificationsContext?.methods.setGeneralNotification({
                    message: DEFAULT_ERROR_MESSAGE,
                });
            })
            .finally(() => {
                frontendContext?.methods.setLookupOngoing(false);
            });
    } else {
        notificationsContext?.methods.setGeneralNotification({});
        userContext?.methods.lookup.setExistentAccount('');

        if (socialProvider) {
            return Promise.reject();
        }
    }
};

/** RESPONSE HANDLERS **/
/**
 * Handles the response from the api if the user is trying to Login without using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {string} email - The email of the user.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */
export const handleLookupUserResponse = ({
    response,
    email,
    contexts,
}: {
    response: ILookUpUserApiResponseProps;
    email: string;
    contexts: IAllContextsProps;
}) => {
    const { notificationsContext, configContext, userContext } = contexts;
    const analytics = configContext?.analytics;
    const { userId, signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    notificationsContext?.methods.setGeneralNotification({});
    userContext?.methods.lookup.setSocialConnected([]);

    if (!!userId) {
        userContext?.methods.lookup.setExistentAccount(email);

        if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
            trackEnterEmailExistingAccount({ analytics });
            userContext?.methods.setUserAccountTypes(AccountTypes.hardAccount);

            configContext?.lookupMethods?.success?.WITH_USER_ID?.HAS_PASSWORD?.();
        } else if (isEmpty(signInMethods)) {
            userContext?.methods.setUserAccountTypes(AccountTypes.softAccount);

            handleUserIsSoftAccount({ contexts });
        } else {
            userContext?.methods.setUserAccountTypes(
                AccountTypes.socialAccount,
            );
            handleUserIsSocial({
                signInMethods,
                contexts,
            });
        }
    } else {
        handleUserNonExistent({ contexts });
    }
};

export const lookupUserForLinking = ({
    formData,
    accessToken,
    contexts,
}: {
    formData: { email: string };
    accessToken: string;
    contexts: IAllContextsProps;
}) => {
    const setGeneralNotification =
        contexts?.notificationsContext?.methods.setGeneralNotification;
    setGeneralNotification?.({});
    const setLookupOngoing =
        contexts?.frontendContext?.methods?.setLookupOngoing;
    setLookupOngoing?.(true);

    contexts.configContext?.socialMethods?.linking?.loookup?.attempt?.();

    lookUpUser({ email: formData.email })
        .then((response: any) => {
            setLookupOngoing?.(false);
            contexts.configContext?.socialMethods?.linking?.loookup?.success?.(
                response,
            );

            handleLookupUserResponseForLinking({
                response,
                email: formData.email,
                accessToken,
                contexts,
            });
        })
        .catch((error: any) => {
            setLookupOngoing?.(false);
            contexts.configContext?.socialMethods?.linking?.loookup?.error?.(
                error,
            );

            if (error.type == 'INVALID_TOKEN') {
                setGeneralNotification?.({
                    message: PASSWORD_TOKEN_EXPIRED_MESSAGE,
                });
            } else {
                setGeneralNotification?.({ message: error?.message });
            }
        });
};
